import { UserProfile } from '../types';
import { sanitizeIc, sanitizePhone } from './formatters';

const COOKIE_NAME = 'asmm_auth_session_v2';
const DB_NAME = 'asmm_vault';
const STORE_NAME = 'session';

/**
 * Encodes and sets a persistent HTTP cookie for the user session.
 * Standard Chrome 'Clear Cache' extensions do not clear cookies by default,
 * ensuring the user stays logged in across browser cache purges.
 */
export function setSessionCookie(user: UserProfile): void {
  if (typeof document === 'undefined') return;
  try {
    const compact: Partial<UserProfile> = {
      id: user.id,
      name: user.name,
      icNo: user.icNo ? sanitizeIc(user.icNo) : '',
      email: user.email,
      phone: user.phone ? sanitizePhone(user.phone) : '',
      role: user.role,
      roles: user.roles || (user.role ? [user.role] : ['Ahli']),
      gelanggang: user.gelanggang,
      bengkung: user.bengkung,
      bengkungBadge: user.bengkungBadge,
      photoUrl: user.photoUrl,
      status: user.status,
      joinedDate: user.joinedDate,
      parentId: user.parentId,
      relationship: user.relationship,
      firebaseUid: user.firebaseUid,
      authProvider: user.authProvider,
      address: user.address,
      onboardingStatus: user.onboardingStatus,
      isOldMember: user.isOldMember
    };
    const jsonStr = JSON.stringify(compact);
    const encoded = encodeURIComponent(jsonStr);
    // 365 days expiration, available across whole domain
    document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=31536000; SameSite=Lax`;
  } catch (err) {
    console.warn('setSessionCookie error:', err);
  }
}

/**
 * Reads and decodes the user session from persistent cookies.
 */
export function getSessionCookie(): UserProfile | null {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`));
    if (match && match[1]) {
      const decoded = decodeURIComponent(match[1]);
      const parsed = JSON.parse(decoded);
      if (parsed && (parsed.id || parsed.icNo)) {
        return parsed as UserProfile;
      }
    }
  } catch (err) {
    console.warn('getSessionCookie error:', err);
  }
  return null;
}

/**
 * Clears the session cookie when user explicitly clicks logout.
 */
export function clearSessionCookie(): void {
  if (typeof document === 'undefined') return;
  try {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
    document.cookie = `asmm_auth_session=; path=/; max-age=0; SameSite=Lax`;
  } catch {}
}

/**
 * Lightweight IndexedDB storage for offline & cache-purge resilience.
 */
function openSessionDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB not supported'));
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function setSessionIndexedDB(user: UserProfile): Promise<void> {
  try {
    const db = await openSessionDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(user, 'active_user');
  } catch {}
}

export async function getSessionIndexedDB(): Promise<UserProfile | null> {
  try {
    const db = await openSessionDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get('active_user');
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function clearSessionIndexedDB(): Promise<void> {
  try {
    const db = await openSessionDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete('active_user');
  } catch {}
}

/**
 * Returns the currently authenticated user from multi-tier storage:
 * 1. LocalStorage (Primary)
 * 2. SessionStorage (Active tab backup)
 * 3. Cookie (Immune to Chrome clear-cache extensions!)
 * 
 * Automatically re-heals LocalStorage if a Clear Cache action wiped it.
 */
export function getStoredUserSession(): UserProfile | null {
  if (typeof window === 'undefined') return null;

  // If user explicitly logged out, respect it
  const isExplicitLogout =
    localStorage.getItem('asmm_user_logged_out') === 'true' ||
    sessionStorage.getItem('asmm_user_logged_out') === 'true';

  if (isExplicitLogout) {
    return null;
  }

  // Tier 1: LocalStorage
  try {
    const raw = localStorage.getItem('asmm_active_user_v2');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.id || parsed.icNo)) {
        // Ensure persistent cookie and sessionStorage are also seeded for existing users
        try {
          if (!getSessionCookie()) {
            setSessionCookie(parsed);
          }
          if (!sessionStorage.getItem('asmm_active_user_v2')) {
            sessionStorage.setItem('asmm_active_user_v2', JSON.stringify(parsed));
          }
        } catch {}
        return parsed;
      }
    }
  } catch {}

  // Tier 2: SessionStorage
  try {
    const sessionRaw = sessionStorage.getItem('asmm_active_user_v2');
    if (sessionRaw) {
      const parsed = JSON.parse(sessionRaw);
      if (parsed && (parsed.id || parsed.icNo)) {
        // Auto-heal LocalStorage and Cookie
        try {
          localStorage.setItem('asmm_active_user_v2', JSON.stringify(parsed));
          localStorage.removeItem('asmm_user_logged_out');
          if (!getSessionCookie()) {
            setSessionCookie(parsed);
          }
        } catch {}
        return parsed;
      }
    }
  } catch {}

  // Tier 3: Persistent Cookie (Survives Chrome clear cache extensions)
  const cookieUser = getSessionCookie();
  if (cookieUser && (cookieUser.id || cookieUser.icNo)) {
    // Auto-heal LocalStorage and SessionStorage
    try {
      localStorage.setItem('asmm_active_user_v2', JSON.stringify(cookieUser));
      sessionStorage.setItem('asmm_active_user_v2', JSON.stringify(cookieUser));
      localStorage.removeItem('asmm_user_logged_out');
    } catch {}
    return cookieUser;
  }

  return null;
}

/**
 * Synchronously or asynchronously saves user across all storage tiers.
 */
export function persistUserSession(user: UserProfile | null): void {
  if (user) {
    // 1. LocalStorage
    try {
      localStorage.setItem('asmm_active_user_v2', JSON.stringify(user));
      localStorage.removeItem('asmm_user_logged_out');
    } catch {}

    // 2. SessionStorage
    try {
      sessionStorage.setItem('asmm_active_user_v2', JSON.stringify(user));
      sessionStorage.removeItem('asmm_user_logged_out');
    } catch {}

    // 3. Persistent Cookie (Survives cache purges)
    setSessionCookie(user);

    // 4. IndexedDB
    setSessionIndexedDB(user).catch(() => {});
  } else {
    // User explicitly logged out: clean all tiers
    try {
      localStorage.removeItem('asmm_active_user_v2');
      localStorage.setItem('asmm_user_logged_out', 'true');
    } catch {}

    try {
      sessionStorage.removeItem('asmm_active_user_v2');
      sessionStorage.setItem('asmm_user_logged_out', 'true');
    } catch {}

    clearSessionCookie();
    clearSessionIndexedDB().catch(() => {});
  }
}
